import { useMemo, useState } from 'react';
import {
  Box, Divider, IconButton, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem, Stack, Tooltip, Typography,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { Check, HelpCircle, Menu as MenuIcon, Moon, MoreVertical, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { design } from '../../theme/clinveaDesign';
import { LanguageSwitcher } from '../common/LanguageSwitcher';
import { useAppLanguage } from '../../i18n/useAppLanguage';

export interface AppTopBarProps {
  mode: 'light' | 'dark' | string | undefined;
  onToggleColorMode: () => void;
  onHelp: () => void;
  /** Route path → label, so the crumb names the screen the user is actually on. */
  navLabels: Map<string, string>;
  /** Rendered between the crumb and the utilities: search, branch switcher, bell, AI chat. */
  children?: React.ReactNode;
  /** Clinic session line, e.g. "OPD 1 · 09:00–14:00". Hidden when not supplied. */
  shiftLabel?: string | null;
  /** Phones and small tablets: opens the navigation drawer (there is no sidebar rail there). */
  onOpenMenu?: () => void;
}

/**
 * The application top bar.
 *
 * <p>This deliberately reverses an earlier decision. The top bar had been removed application-wide
 * and its controls (search, branch switcher, notifications, AI chat, theme) moved into a utility
 * strip at the bottom of the sidebar. The approved design reinstates it, so those controls move
 * back up here — they are passed in as `children` rather than re-implemented, and the sidebar strip
 * is reduced to the collapse toggle, so there is still exactly one instance of each control.
 *
 * <p>On a phone the bar has room for the menu button, the screen name and the everyday controls
 * (search, branch, notifications). Language, theme and help move into one "More" menu there rather
 * than being squeezed off the edge.
 */
export function AppTopBar({
  mode, onToggleColorMode, onHelp, navLabels, children, shiftLabel, onOpenMenu,
}: AppTopBarProps) {
  const { t } = useTranslation('common');
  const d = design(mode);
  const location = useLocation();
  const { code, languages, setLanguage } = useAppLanguage();
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);

  // Longest matching nav path wins, so /admin/fee-schedule resolves to the fee-schedule entry
  // rather than to /admin. Falls back to nothing rather than guessing a label from the URL.
  const crumb = useMemo(() => {
    let best: string | null = null;
    let bestLen = -1;
    navLabels.forEach((label, path) => {
      if ((location.pathname === path || location.pathname.startsWith(path + '/')) && path.length > bestLen) {
        best = label;
        bestLen = path.length;
      }
    });
    return best;
  }, [location.pathname, navLabels]);

  const squareBtn = { border: '1px solid', borderColor: d.line, borderRadius: '8px', color: d.muted, width: 34, height: 34 } as const;

  return (
    <Box
      component="header"
      sx={{
        flexShrink: 0,
        height: 56,
        bgcolor: d.surface,
        borderBottom: '1px solid',
        borderColor: d.line,
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, sm: 1.75 },
        px: { xs: 1, sm: 2.25 },
        position: { xs: 'sticky', md: 'static' },
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        transition: 'background-color .3s ease, border-color .3s ease',
      }}
    >
      {onOpenMenu && (
        <IconButton onClick={onOpenMenu} aria-label={t('nav:action.openMenu')} sx={{ color: d.ink, flexShrink: 0 }}>
          <MenuIcon size={22} />
        </IconButton>
      )}

      {crumb && (
        // Hidden on phones: the page shows its own heading, and the always-visible search needs the room.
        <Typography sx={{ fontSize: '0.82rem', color: d.faint, flexShrink: 1, minWidth: 0, display: { xs: 'none', sm: 'block' } }} noWrap>
          <Box component="span" sx={{ color: d.ink, fontWeight: 600 }}>{crumb}</Box>
        </Typography>
      )}

      {/* Search and the other utilities, supplied by Layout so this component owns no state. */}
      <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1 }}
        sx={{ flex: 1, minWidth: 0, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
        {children}
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
        {shiftLabel && (
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.9,
              fontSize: '0.76rem', color: d.muted,
              border: '1px solid', borderColor: d.line, borderRadius: '8px',
              px: 1.4, py: 0.85, bgcolor: d.surface, whiteSpace: 'nowrap',
            }}
          >
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: d.green, flexShrink: 0 }} />
            {shiftLabel}
          </Box>
        )}

        <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1 }}>
          <LanguageSwitcher buttonSx={squareBtn} />

          <Tooltip title={mode === 'dark' ? t('theme.toggleLight') : t('theme.toggleDark')}>
            <IconButton onClick={onToggleColorMode} size="small" sx={squareBtn}>
              {mode === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </IconButton>
          </Tooltip>

          <Tooltip title={t('nav:action.help')}>
            <IconButton onClick={onHelp} size="small" sx={squareBtn}>
              <HelpCircle size={16} />
            </IconButton>
          </Tooltip>
        </Box>

        <IconButton
          onClick={(e) => setMoreAnchor(e.currentTarget)}
          aria-label={t('nav:action.more')}
          aria-haspopup="menu"
          size="small"
          sx={{ ...squareBtn, display: { xs: 'inline-flex', sm: 'none' } }}
        >
          <MoreVertical size={16} />
        </IconButton>
      </Stack>

      <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }} transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        PaperProps={{ sx: { minWidth: 230, borderRadius: 2.5 } }}>
        <MenuItem onClick={() => { onToggleColorMode(); setMoreAnchor(null); }}>
          <ListItemIcon>{mode === 'dark' ? <Sun size={16} /> : <Moon size={16} />}</ListItemIcon>
          <ListItemText>{mode === 'dark' ? t('theme.toggleLight') : t('theme.toggleDark')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { onHelp(); setMoreAnchor(null); }}>
          <ListItemIcon><HelpCircle size={16} /></ListItemIcon>
          <ListItemText>{t('nav:action.help')}</ListItemText>
        </MenuItem>
        <Divider />
        <ListSubheader sx={{ lineHeight: 2.4, bgcolor: 'transparent' }}>{t('language.changeLanguage')}</ListSubheader>
        {languages.map((lang) => (
          <MenuItem key={lang.code} selected={lang.code === code} onClick={() => { setLanguage(lang.code); setMoreAnchor(null); }}>
            <ListItemText primary={<Typography lang={lang.code} sx={{ fontWeight: lang.code === code ? 700 : 500 }}>{lang.label}</Typography>} />
            {lang.code === code && <Check size={16} />}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
